/**
 * AccountCmpButton component.
 */
import Button from '@components/atoms/buttons/confirmButton';
import CmpUtils from '@shared/utils/cmp';
import Container from '@components/atoms/container/accountCmpContainer';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import Item from './accountItem';
import ScreenUtils from '@screen';
import Spinner from '../spinner/spinner';
import TranslationManager from '@shared/assets/locale/translationsManager';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { focusSelector } from '@shared/store/slices/focus.slice';
import { magicRemoteSelector } from '@shared/store/slices/magicRemote.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useCallback, useMemo, useState } from 'react';

/**
 * Constants
 * LITERALS - The literals for the account CMP button
 */
const LITERALS = 'PAGES.SETTINGS.ACCOUNT';

/**
 * Handles the CMP button display.
 */
export default function AccountCmpButton() {
  const { display, callbacks, props, text } = useAccountCmpButton();

  // Render only if CMP is enabled
  if (!display.cmpButton) return null;

  // Render the account CMP button
  return (
    <Container>
      <Item {...props.item} />
      <Button {...props.button} {...callbacks.button}>
        {text.configure}
      </Button>
      {display.spinner ? <Spinner {...props.spinner} /> : null}
    </Container>
  );
}

/**
 * Handles the account CMP button logic and returns the props for the account CMP button.
 */
function useAccountCmpButton() {
  // State and selector hooks

  const { focus } = useAppSelector(focusSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [displaySpinner, setDisplaySpinner] = useState(false);

  // Callbacks

  const openCmp = useCallback(() => {
    if (FlavourConfig.capabilities.cmp) {
      setDisplaySpinner(true);
      CmpUtils.openCmp(() => setDisplaySpinner(false));
    }
  }, []);

  // Keyboard hook

  useKeyboard({
    focus: Focus.ACCOUNT,
    keyDownHandler: (key) => {
      switch (key) {
        case 'SELECT':
          openCmp();
          break;
        default:
          break;
      }
    },
  });

  // Memoized values

  return useMemo(() => {
    const menuFocused = focus === Focus.MENU;
    const text = {
      configure: TranslationManager.get(`${LITERALS}.DATA.CONFIGURE`),
      privacyPolicy: TranslationManager.get(`${LITERALS}.DATA.PRIVACY_POLICY`),
    };
    return {
      display: {
        cmpButton: FlavourConfig.capabilities.cmp,
        spinner: displaySpinner,
      },
      callbacks: {
        button: {
          onClick: openCmp,
          onPointerEnter: () => setButtonHovered(true),
          onPointerLeave: () => setButtonHovered(false),
        },
      },
      props: {
        button: {
          focused: (!menuFocused && !enabled) || buttonHovered,
          fontSize: 36,
          height: 80,
          margin: `0 0 0 ${20 * ScreenUtils.widthRatio}px`,
          width: 300,
        },
        item: {
          displayTitleOnly: true,
          title: text.privacyPolicy,
        },
        spinner: {
          type: 'account',
        },
      },
      text,
    };
  }, [buttonHovered, displaySpinner, enabled, focus, openCmp]);
}
